import React, {useState, useMemo, useEffect, Fragment, useRef} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'

// Chakra Components
import {
    Box,
    Stack,
    Grid,
    GridItem,
    Container,
    useDisclosure,
    Button,
    Divider,
    Flex,
    Image,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Note: Using text symbols instead of icons to avoid import issues

// Project Components
import CartSecondaryButtonGroup from '../../pages/cart/partials/cart-secondary-button-group/index'
import ConfirmationModal from '../../components/confirmation-modal/index'
import EmptyCart from '../cart/partials/empty-cart/index'
import OrderSummary from '../../components/order-summary/index'
import BundleProductViewModal from '@salesforce/retail-react-app/app/components/product-view-modal/bundle'
import RecommendedProducts from '../../components/recommended-products'
import {useLocation} from 'react-router-dom'

// Hooks
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useWishList} from '../../hooks/use-wish-list'

// Constants
import {
    API_ERROR_MESSAGE,
    EINSTEIN_RECOMMENDERS,
    TOAST_ACTION_VIEW_WISHLIST,
    TOAST_MESSAGE_ADDED_TO_WISHLIST,
    TOAST_MESSAGE_REMOVED_ITEM_FROM_CART,
    TOAST_MESSAGE_ALREADY_IN_WISHLIST
} from '@salesforce/retail-react-app/app/constants'
import {CART_LIMIT_ERROR_CODE, CART_FULL_ERROR_MESSAGE} from '../../constants'
import {REMOVE_CART_ITEM_CONFIRMATION_DIALOG_CONFIG} from '../cart/partials/cart-secondary-button-group/index'

// Utilities
import debounce from 'lodash/debounce'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {
    useShopperBasketsMutation,
    useShippingMethodsForShipment,
    useProducts,
    useShopperCustomersMutation,
    useCategory,
    useShopperBasketsMutationHelper,
    useUsid
} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import UnavailableProductConfirmationModal from '@salesforce/retail-react-app/app/components/unavailable-product-confirmation-modal'
import {getUpdateBundleChildArray} from '@salesforce/retail-react-app/app/utils/product-utils'
import {pxToRem} from '../../utills/pxToRem'
import {getUrlSearchParam} from '../../utills/url'
import useSitePreference from '../../hooks/use-site-preference'
import colors from '../../theme/foundations/colors'
import QuickView from '../product-list/partials/quick-view'
import {CART_RECOMMENDATION} from '../../constants'
import ContentRenderer from '../../components/content-renderer'
import {cartBannerStyle, dividorStyle} from './index.style'
import ProductItem from '../../components/product-item'
import {transformBasketData, transformGiftCertificateItems} from '../../utills/transformBasket'
import Link from '@salesforce/retail-react-app/app/components/link'
import {AmexIcon, DiscoverIcon, MastercardIcon, PaypalIcon, VisaIcon} from '../../components/icons'
import {
    checkoutButtonStyle,
    checkoutButtonDisabledStyle,
    iconStyle
} from '../../components/order-summary/index.style'

const DEBOUNCE_WAIT = 750

import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {errorMessageStyle} from '../../components/product-item/index.style'
import CartTitle from './partials/cart-title'
import CartSkeleton from './partials/cart-skeleton'
import PropTypes from 'prop-types'
import {GlobalErrorProvider, useGlobalError} from './util/global-error-context'
import GlobalErrorMessage from './partials/global-cart-error'
import useCartLimit from '../../hooks/use-cart-limit'
import CartErrorMessage from '../../components/CartErrorMessage'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
/* -----------------Adyen Begin ------------------------ */
import '@adyen/adyen-salesforce-pwa/dist/app/adyen.css'
import {useAccessToken, useCustomerId} from '@salesforce/commerce-sdk-react'
import GooglePayExpress from '../checkout/components/express-checkout/googlePay/index'
import ApplePayExpress from '../checkout/components/express-checkout/applePay/index'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import AdyenExpressCheckoutProvider from '../checkout/adyenOverrides/contexts/adyen-express-checkout-context'
/* -----------------Adyen End ------------------------ */

const CartCta = ({basket, updateBasket, isMixedCart, mixedCartStaticMessage, shippingMethods}) => {
    const customerId = useCustomerId()
    const {getTokenWhenReady} = useAccessToken()
    const {locale, site} = useMultiSite()
    const navigate = useNavigation()
    const globalErrorContext = useGlobalError()
    const hasGlobalError = globalErrorContext?.hasGlobalError || false
    const {formatMessage} = useIntl()
    const toast = useToast()
    const {app: appConfig} = getConfig()
    const [authToken, setAuthToken] = useState()
    const handleClick = async (event) => {
        event.preventDefault()

        if (hasGlobalError) {
            return false
        }

        if (!basket?.basketId) {
            toast({
                title: formatMessage({
                    defaultMessage: 'No basket available',
                    id: 'cart_cta.error.no_basket'
                }),
                status: 'error'
            })
            return false
        }

        try {
            await updateBasket.mutateAsync({
                parameters: {basketId: basket.basketId},
                body: {
                    c_isCart: true
                }
            })
            navigate('/checkout')
        } catch (error) {
            window.location.reload()
            return false
        }
    }

    const skus = [
        ...(basket?.productItems?.map((p) => p.productId) ?? []),
        ...(basket?.giftCertificateItems?.length ? ['EGC'] : [])
    ].join(',')

    useEffect(() => {
        const getToken = async () => {
            const token = await getTokenWhenReady()
            setAuthToken(token)
        }

        getToken()
    }, [])
    if (!authToken) {
        return
    }
    return (
        <>
            {isMixedCart && mixedCartStaticMessage && (
                <Flex flexDirection="row" gap="5px" mt="10px">
                    <Image
                        src={getAssetUrl('static/img/header/error-symbol.svg')}
                        alt="Error symbol"
                        sx={{w: '13px', h: '13px', mt: '4px'}}
                    />
                    <Text sx={{...errorMessageStyle, lineHeight: '20px', m: 0}}>
                        {mixedCartStaticMessage}
                    </Text>
                </Flex>
            )}
            <Button
                onClick={handleClick}
                disabled={hasGlobalError}
                sx={hasGlobalError ? checkoutButtonDisabledStyle : checkoutButtonStyle}
            >
                <FormattedMessage
                    defaultMessage="Proceed to Checkout"
                    id="cart_cta.link.checkout"
                />
            </Button>
            {appConfig.afterPay.enableMessaging &&
                appConfig.afterPay.cartMPID &&
                appConfig.afterPay.cartPlacementID && (
                    <square-placement
                        data-mpid={appConfig.afterPay.cartMPID}
                        data-placement-id={appConfig.afterPay.cartPlacementID}
                        data-page-type="cart"
                        data-amount={basket?.orderTotal}
                        data-currency={basket.currency}
                        data-consumer-locale="en_US"
                        data-item-skus={skus}
                        data-cart-is-eligible="true"
                    ></square-placement>
                )}

            {basket && basket?.orderTotal > 0 && !isMixedCart && (
                <AdyenExpressCheckoutProvider
                    authToken={authToken}
                    customerId={customerId}
                    locale={locale}
                    site={site}
                    basket={basket}
                    navigate={navigate}
                    isMiniCart={false}
                >
                    <GooglePayExpress shippingMethods={shippingMethods} />
                    <ApplePayExpress shippingMethods={shippingMethods} />
                </AdyenExpressCheckoutProvider>
            )}
            <Flex justify={'center'} sx={iconStyle}>
                <VisaIcon height={8} width={10} mr={2} />
                <MastercardIcon height={8} width={10} mr={2} />
                <AmexIcon height={8} width={10} mr={2} />
                <DiscoverIcon height={8} width={10} mr={2} />
                <PaypalIcon height={8} width={10} mr={2} />
            </Flex>
        </>
    )
}

CartCta.propTypes = {
    basket: PropTypes.object,
    updateBasket: PropTypes.object,
    isMixedCart: PropTypes.bool,
    mixedCartStaticMessage: PropTypes.string,
    shippingMethods: PropTypes.any
}

const Cart = () => {
    const {
        data: basket = {},
        derivedData: {totalItems},
        isLoading
    } = useCurrentBasket()
    const {isAtLimit, errorMessage: cartLimitErrorMessage} = useCartLimit()
    const {res} = useServerContext()
    if (res) {
        res.set('Cache-Control', 'private, no-cache')
    }
    const {data} = useSitePreference('recommendedForYou')
    const updateBasket = useShopperBasketsMutation('updateBasket')
    const createBasket = useShopperBasketsMutation('createBasket')
    const {data: statesMappingData} = useSitePreference('statesMapping')
    const recommendedForYou = data?.recommendedForYou
    const statesMapping = statesMappingData?.statesMapping
    const productIds = basket?.productItems?.map(({productId}) => productId).join(',') ?? ''
    const location = useLocation()
    const {usid} = useUsid()
    const hasFired = useRef()
    // Preserve basePrice across basket updates to prevent losing original prices
    const preservedBasePricesRef = useRef(new Map())
    const [shippingOptions, setShippingOptions] = useState()

    useEffect(() => {
        if (basket?.basketId) {
            const updateBody = {
                c_isCart: true
            }

            updateBasket.mutate({
                parameters: {basketId: basket.basketId},
                body: updateBody
            })
        }
    }, [basket?.basketId])

    const {data: products, isLoading: isProductsLoading} = useProducts(
        {
            parameters: {
                ids: productIds,
                allImages: true,
                perPricebook: true,
                c_storepickup: true,
                c_isPDPRequest: true,
                locale: 'default'
            }
        },
        {
            enabled: Boolean(productIds),
            keepPreviousData: true,
            select: (result) => {
                return result?.data?.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})
            }
        }
    )

    const {data: customer} = useCurrentCustomer()
    const {customerId, isRegistered} = customer

    /***************** Product Bundles ************************/
    const bundleChildVariantIds = []
    basket?.productItems?.forEach((productItem) => {
        productItem?.bundledProductItems?.forEach((childProduct) => {
            bundleChildVariantIds.push(childProduct.productId)
        })
    })

    const {data: bundleChildProductData} = useProducts(
        {
            parameters: {
                ids: bundleChildVariantIds?.join(','),
                allImages: false,
                expand: ['availability', 'variations'],
                select: '(data.(id,inventory))'
            }
        },
        {
            enabled: bundleChildVariantIds?.length > 0,
            keepPreviousData: true,
            select: (result) => {
                return result?.data?.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})
            }
        }
    )

    // We use the `products` object to reference products by itemId instead of productId
    // Since with product bundles, even though the parent productId is the same,
    // variant selection of the bundle children can be different,
    // and require unique references to each product bundle
    const productsByItemId = useMemo(() => {
        const updateProductsByItemId = {}
        basket?.productItems?.forEach((productItem) => {
            let currentProduct = products?.[productItem?.productId]

            // calculate inventory for product bundles based on availability of children
            if (productItem?.bundledProductItems && bundleChildProductData) {
                let lowestStockLevel =
                    currentProduct?.inventory?.stockLevel ?? Number.MAX_SAFE_INTEGER
                let productWithLowestInventory = ''
                productItem?.bundledProductItems.forEach((bundleChild) => {
                    const bundleChildStockLevel =
                        bundleChildProductData?.[bundleChild.productId]?.inventory?.stockLevel ??
                        Number.MAX_SAFE_INTEGER
                    lowestStockLevel = Math.min(lowestStockLevel, bundleChildStockLevel)
                    if (lowestStockLevel === bundleChildStockLevel)
                        productWithLowestInventory = bundleChild.productName
                })

                if (currentProduct?.inventory) {
                    currentProduct = {
                        ...currentProduct,
                        inventory: {
                            ...currentProduct.inventory,
                            stockLevel: lowestStockLevel,
                            lowestStockLevelProductName: productWithLowestInventory
                        }
                    }
                }
            }
            updateProductsByItemId[productItem.itemId] = currentProduct
        })
        return updateProductsByItemId
    }, [basket, products, bundleChildProductData])

    const getInventoryErrorType = (quantity, stockLevel) => {
        if (stockLevel <= 0) {
            return 'OUT_OF_STOCK'
        }
        if (quantity > stockLevel) {
            return 'QUANTITY_EXCEEDED'
        }
        return null
    }

    /*****************Basket Mutation************************/
    const updateItemInBasketMutation = useShopperBasketsMutation('updateItemInBasket')
    const updateItemsInBasketMutation = useShopperBasketsMutation('updateItemsInBasket')
    const removeItemFromBasketMutation = useShopperBasketsMutation('removeItemFromBasket')
    const updateShippingMethodForShipmentsMutation = useShopperBasketsMutation(
        'updateShippingMethodForShipment'
    )
    const {mutateAsync: removeGiftCertificateItemFromBasket} = useShopperBasketsMutation(
        'removeGiftCertificateItemFromBasket'
    )
    /*****************Basket Mutation************************/

    const [selectedItem, setSelectedItem] = useState(undefined)
    const [localQuantity, setLocalQuantity] = useState({})
    const [localIsGiftItems, setLocalIsGiftItems] = useState({})
    const [isCartItemLoading, setCartItemLoading] = useState(false)
    const [quantityStates, setQuantityStates] = useState({})

    const {isOpen, onOpen, onClose} = useDisclosure()
    const {formatMessage} = useIntl()
    const toast = useToast()
    const navigate = useNavigation()
    const modalProps = useDisclosure()
    const {data: sessionPrefData} = useSitePreference('sessionDrivenProductLists')

    /******************* Shipping Methods for basket shipment *******************/
    // do this action only if the basket shipping method is not defined
    // we need to fetch the shippment methods to get the default value before we can add it to the basket
    useShippingMethodsForShipment(
        {
            parameters: {
                basketId: basket?.basketId,
                shipmentId: 'me',
                c_isCart: true
            }
        },
        {
            // only fetch if basket is has no shipping method in the first shipment
            enabled:
                !!basket?.basketId &&
                basket?.shipments?.length > 0 &&
                (basket?.productItems?.length > 0 || basket?.giftCertificateItems?.length > 0) &&
                !basket?.shipments[0]?.shippingMethod,
            onSuccess: (data) => {
                setShippingOptions(data)
                updateShippingMethodForShipmentsMutation.mutate({
                    parameters: {
                        basketId: basket?.basketId,
                        shipmentId: 'me'
                    },
                    body: {
                        id: data.defaultShippingMethodId
                    }
                })
            }
        }
    )

    /************************* Error handling ***********************/
    const showError = () => {
        toast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error'
        })
    }
    /************************* Error handling ***********************/

    /**************** Wishlist ****************/
    const {data: wishlist} = useWishList()

    const createCustomerProductListItem = useShopperCustomersMutation(
        'createCustomerProductListItem'
    )
    const handleAddToWishlist = async (product) => {
        try {
            if (!customerId || !wishlist) {
                return
            }

            const isItemInWishlist = wishlist?.customerProductListItems?.find(
                (i) => i.productId === product?.originalProductId
            )

            if (!isItemInWishlist) {
                await createCustomerProductListItem.mutateAsync({
                    parameters: {
                        listId: wishlist.id,
                        customerId
                    },
                    body: {
                        // NOTE: APi does not respect quantity, it always adds 1
                        quantity: product.quantity,
                        productId: product.productId,
                        public: false,
                        priority: 1,
                        type: 'product'
                    }
                })
                toast({
                    title: formatMessage(TOAST_MESSAGE_ADDED_TO_WISHLIST, {quantity: 1}),
                    status: 'success',
                    action: (
                        // it would be better if we could use <Button as={Link}>
                        // but unfortunately the Link component is not compatible
                        // with Chakra Toast, since the ToastManager is rendered via portal
                        // and the toast doesn't have access to intl provider, which is a
                        // requirement of the Link component.
                        <Button variant="link" onClick={() => navigate('/account/wishlist')}>
                            {formatMessage(TOAST_ACTION_VIEW_WISHLIST)}
                        </Button>
                    )
                })
            } else {
                toast({
                    title: formatMessage(TOAST_MESSAGE_ALREADY_IN_WISHLIST),
                    status: 'info',
                    action: (
                        <Button variant="link" onClick={() => navigate('/account/wishlist')}>
                            {formatMessage(TOAST_ACTION_VIEW_WISHLIST)}
                        </Button>
                    )
                })
            }
        } catch {
            showError()
        }
    }

    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    /**************** Wishlist ****************/
    const handleAddToCart = async (productSelectionValues) => {
        try {
            const productItems = productSelectionValues?.map(
                ({productId, price, quantity, c_storeId, c_storepickup}) => ({
                    productId,
                    price,
                    quantity,
                    c_storeId,
                    c_storepickup
                })
            )

            await addItemToNewOrExistingBasket(productItems)

            return productSelectionValues
        } catch (error) {
            let errorBody
            if (error?.response instanceof Response) {
                try {
                    errorBody = await error.response.clone().json()
                } catch {
                    const text = await error.response.text()
                    try {
                        errorBody = JSON.parse(text)
                    } catch {
                        errorBody = {message: text}
                    }
                }
            }

            if (errorBody?.statusCode === CART_LIMIT_ERROR_CODE) {
                // Cart is full - 24 product limit reached
                toast({
                    title: CART_FULL_ERROR_MESSAGE,
                    status: 'error'
                })
            } else if (errorBody?.statusCode === 'BUS-1001') {
                // Wishlist related error
                toast({
                    title: errorBody?.statusMessage,
                    status: 'info',
                    action: (
                        <Button variant="link" onClick={() => navigate('/account/wishlist')}>
                            {''}
                        </Button>
                    )
                })
            } else {
                showError(error)
            }
        }
    }

    /***************************** Update Cart **************************/
    const handleUpdateCart = async (variant, quantity, storeId, storepickup) => {
        // close the modal before handle the change

        onClose()
        // using try-catch is better than using onError callback since we have many mutation calls logic here
        try {
            setCartItemLoading(true)
            if (
                selectedItem?.productId !== variant?.productId ||
                selectedItem?.c_storepickup !== storepickup
            ) {
                const item = {
                    productId: variant.productId,
                    quantity,
                    price: variant.price,
                    c_storeId: storeId,
                    c_storepickup: storepickup
                }
                await removeItemFromBasketMutation.mutateAsync({
                    parameters: {
                        basketId: basket.basketId,
                        itemId: selectedItem.itemId
                    }
                })
                await handleAddToCart([item])
            } else if (selectedItem.quantity !== quantity) {
                return await changeItemQuantity(quantity, selectedItem)
            }
        } catch {
            showError()
        } finally {
            setCartItemLoading(false)
            setSelectedItem(undefined)
            navigate('/cart/')
        }
    }

    const handleUpdateBundle = async (bundle, bundleQuantity, childProducts) => {
        // close the modal before handle the change
        onClose()

        try {
            setCartItemLoading(true)
            const itemsToBeUpdated = getUpdateBundleChildArray(bundle, childProducts)

            // We only update the parent bundle when the quantity changes
            // Since top level bundles don't have variants
            if (bundle.quantity !== bundleQuantity) {
                itemsToBeUpdated.unshift({
                    itemId: bundle.itemId,
                    productId: bundle.productId,
                    quantity: bundleQuantity
                })
            }

            if (itemsToBeUpdated.length) {
                await updateItemsInBasketMutation.mutateAsync({
                    method: 'PATCH',
                    parameters: {
                        basketId: basket.basketId
                    },
                    body: itemsToBeUpdated
                })
            }
        } catch {
            showError()
        } finally {
            setCartItemLoading(false)
            setSelectedItem(undefined)
        }
    }

    const handleIsAGiftChange = async (product, checked) => {
        try {
            const previousVal = localIsGiftItems[product.itemId]
            setLocalIsGiftItems({
                ...localIsGiftItems,
                [product.itemId]: checked
            })
            setCartItemLoading(true)
            setSelectedItem(product)
            await updateItemInBasketMutation.mutateAsync(
                {
                    parameters: {basketId: basket?.basketId, itemId: product.itemId},
                    body: {
                        productId: product.id,
                        quantity: parseInt(product.quantity),
                        gift: checked
                    }
                },
                {
                    onSettled: () => {
                        // reset the state
                        setCartItemLoading(false)
                        setSelectedItem(undefined)
                    },
                    onSuccess: () => {
                        setLocalIsGiftItems({...localIsGiftItems, [product.itemId]: undefined})
                    },
                    onError: () => {
                        // reset the quantity to the previous value
                        setLocalIsGiftItems({...localIsGiftItems, [product.itemId]: previousVal})
                        showError()
                    }
                }
            )
        } catch (e) {
            showError()
        } finally {
            setCartItemLoading(false)
            setSelectedItem(undefined)
        }
    }

    const handleUnavailableProducts = async (unavailableProductIds) => {
        const productItems = basket?.productItems?.filter((item) =>
            unavailableProductIds?.includes(item.productId)
        )

        await Promise.all(
            productItems.map(async (item) => {
                await handleRemoveItem(item)
            })
        )
    }

    // Data Layer Event (increase/decrease quantity, add/remove from cart)
    const [dataLayerProductId, setDataLayerProductId] = useState(null)
    const [pendingDataLayerEvent, setPendingDataLayerEvent] = useState(null)

    const productForDataLayer = products && products[dataLayerProductId]

    const {data: category} = useCategory(
        {
            parameters: {
                id: productForDataLayer?.c_primaryCatergoryID,
                locale: 'default'
            }
        },
        {
            enabled: !!productForDataLayer?.c_primaryCatergoryID
        }
    )

    // Fire dataLayer event when product and category are available
    useEffect(() => {
        if (
            pendingDataLayerEvent &&
            products?.[dataLayerProductId] &&
            category &&
            window?.dataLayer
        ) {
            const {quantity, product} = pendingDataLayerEvent
            const productData = products[dataLayerProductId]
            const eventType = quantity > product?.quantity ? 'addToCart' : 'removeFromCart'
            const actionText = eventType === 'addToCart' ? 'Add to Cart' : 'Remove from Cart'

            window.dataLayer.push({
                event: eventType,
                category: 'Product',
                action: actionText,
                currency: basket?.currency,
                price: basket?.orderTotal,
                quantity: totalItems + (basket.giftCertificateItems?.length || 0),
                session_id: usid,
                ecommerce: {
                    [eventType === 'addToCart' ? 'add' : 'remove']: {
                        actionField: {
                            shipType: product?.c_storepickup ? 'BOPIS' : 'Ship'
                        },
                        products: [
                            {
                                name: productData?.name || productData?.productName,
                                id: productData?.master?.masterId || productData?.id,
                                price: productData?.price,
                                brand: productData?.brand,
                                category: category?.parentCategoryTree
                                    .map((cat) => cat.name)
                                    .join('/'),
                                quantity,
                                dimension7: product?.c_storepickup ? 'BOPIS' : 'Ship',
                                ATS:
                                    productsByItemId?.[product?.itemId]?.inventory?.stockLevel ??
                                    null,
                                onlineFlag: true,
                                bopis_type: product?.c_storepickup ? 'BOPIS' : 'Ship',
                                bopis_eligble:
                                    productData?.c_instorePickupOnPWA?.enableInStorePickup,
                                variant: product?.productId
                            }
                        ]
                    }
                }
            })

            setPendingDataLayerEvent(null)
        }
    }, [pendingDataLayerEvent, category, products, productsByItemId])

    // Triggered when an action (e.g., add/remove from cart) is initiated
    const dataLayerEvent = (quantity, product) => {
        setDataLayerProductId(product?.productId)
        setPendingDataLayerEvent({quantity, product})
    }

    /***************************** Update Cart **************************/

    /***************************** Update quantity **************************/
    const changeItemQuantity = debounce(async (quantity, product) => {
        // This local state allows the dropdown to show the desired quantity
        // while the API call to update it is happening.
        const previousQuantity = localQuantity[product.itemId]
        setLocalQuantity({...localQuantity, [product.itemId]: quantity})
        setCartItemLoading(true)
        setSelectedItem(product)

        await updateItemInBasketMutation.mutateAsync(
            {
                parameters: {basketId: basket?.basketId, itemId: product.itemId},
                body: {
                    productId: product.productId,
                    quantity: parseInt(quantity)
                }
            },
            {
                onSettled: () => {
                    // reset the state
                    setCartItemLoading(false)
                    setSelectedItem(undefined)
                },
                onSuccess: () => {
                    setLocalQuantity({...localQuantity, [product.itemId]: undefined})
                    dataLayerEvent(quantity, product)
                },
                onError: () => {
                    // reset the quantity to the previous value
                    setLocalQuantity({...localQuantity, [product.itemId]: previousQuantity})
                    showError()
                }
            }
        )
    }, DEBOUNCE_WAIT)

    const handleChangeItemQuantity = async (product, value) => {
        const stockLevel = productsByItemId?.[product.itemId]?.inventory?.stockLevel ?? 1

        // Handle removing of the items when 0 is selected.
        if (value === 0) {
            changeItemQuantity.flush()
            setSelectedItem(product)
            modalProps.onOpen()
            return false
        }

        changeItemQuantity.cancel()

        // Allow use to selected values above the inventory.
        if (value > stockLevel || value === product.quantity) {
            return true
        }

        // Take action.
        changeItemQuantity(value, product)

        return true
    }

    // Custom quantity picker handler
    const handleQuantityPickerChange = (product, newQuantity) => {
        // Update local state immediately for responsive UI
        setQuantityStates({
            ...quantityStates,
            [product.itemId]: newQuantity
        })

        // Call the existing quantity change handler
        handleChangeItemQuantity(product, newQuantity)
    }

    // Get current quantity for a product (local state or actual quantity)
    const getCurrentQuantity = (product) => {
        return quantityStates[product.itemId] ?? localQuantity[product.itemId] ?? product.quantity
    }
    /***************************** Update quantity **************************/

    /***************************** Remove Item from basket **************************/
    const handleRemoveItem = async (product) => {
        setSelectedItem(product)
        setCartItemLoading(true)
        if (product.isGiftCertificate) {
            await removeGiftCertificateItemFromBasket(
                {
                    parameters: {
                        basketId: basket.basketId,
                        giftCertificateItemId: product.itemId
                    }
                },
                {
                    onSettled: () => {
                        // reset the state
                        setCartItemLoading(false)
                        setSelectedItem(undefined)
                    },
                    onSuccess: () => {
                        toast({
                            title: formatMessage(TOAST_MESSAGE_REMOVED_ITEM_FROM_CART, {
                                quantity: 1
                            }),
                            status: 'success'
                        })
                    },
                    onError: () => {
                        showError()
                    }
                }
            )
        } else {
            await removeItemFromBasketMutation.mutateAsync(
                {
                    parameters: {basketId: basket.basketId, itemId: product.itemId}
                },
                {
                    onSettled: () => {
                        // reset the state
                        setCartItemLoading(false)
                        setSelectedItem(undefined)
                    },
                    onSuccess: () => {
                        toast({
                            title: formatMessage(TOAST_MESSAGE_REMOVED_ITEM_FROM_CART, {
                                quantity: 1
                            }),
                            status: 'success'
                        })
                    },
                    onError: () => {
                        showError()
                    }
                }
            )
        }
    }

    const previousSearchParams = useMemo(() => {
        const {snitizeUrlSearchParam} = getUrlSearchParam(location.search)
        return snitizeUrlSearchParam
    }, [location.search])

    const updateTheBasket = async (sid, isAfterBasketCreate = false, basketRes = {}) => {
        try {
            if (
                isAfterBasketCreate
                    ? !basketRes?.c_cartFromSessionListInitialized
                    : !basket?.c_cartFromSessionListInitialized
            ) {
                await updateBasket.mutateAsync({
                    parameters: {
                        basketId: isAfterBasketCreate ? basketRes?.basketId : basket?.basketId,
                        c_sid: sid
                    },
                    body: {
                        c_isCart: true
                    }
                })
                hasFired.current = true
            }
        } catch (error) {
            console.error('error:', error)
        }
    }

    const createTheBasket = async (sid) => {
        try {
            const basketBody = {}

            const basketRes = await createBasket.mutateAsync({
                parameters: {},
                body: basketBody
            })
            updateTheBasket(sid, true, basketRes)
            hasFired.current = true
        } catch (error) {
            console.error('error:', error)
        }
    }

    useEffect(() => {
        if (location.search) {
            if (hasFired.current == true || isLoading) return

            const searchParams = new URLSearchParams(location.search)
            const sid = searchParams.get('sid')

            if (sid && sessionPrefData?.sessionDrivenProductLists) {
                if (Object.keys(basket)?.length > 0) {
                    updateTheBasket(sid)
                }
                if (Object.keys(basket)?.length === 0) {
                    createTheBasket(sid)
                }
            }
        }
    }, [location.search, basket?.basketId, isLoading, sessionPrefData?.sessionDrivenProductLists])

    // Transform basket data
    const transformedProductData = transformBasketData(products, basket, statesMapping)

    // Preserve basePrice from previous transformation to prevent losing original prices
    // when products are refetching after quantity updates
    const transformedProductDataWithPreservedPrices = useMemo(() => {
        if (!transformedProductData || transformedProductData.length === 0) {
            // Clean up preserved prices if basket is empty
            preservedBasePricesRef.current.clear()
            return transformedProductData
        }

        // Get current item IDs to clean up removed items
        const currentItemIds = new Set(transformedProductData.map((item) => item.itemId || item.id))

        // Remove preserved prices for items that are no longer in the basket
        preservedBasePricesRef.current.forEach((_, itemId) => {
            if (!currentItemIds.has(itemId)) {
                preservedBasePricesRef.current.delete(itemId)
            }
        })

        return transformedProductData.map((item) => {
            // Use itemId as primary key, fallback to id or productId for stability
            // Try multiple identifiers to ensure we find the preserved price
            const itemId = item.itemId || item.id || item.originalProductId
            const productId = item.originalProductId || item.productId
            // Try to get preserved price by itemId first, then by productId as fallback
            let preservedBasePrice = preservedBasePricesRef.current.get(itemId)
            if (!preservedBasePrice && productId && productId !== itemId) {
                preservedBasePrice = preservedBasePricesRef.current.get(productId)
            }
            const currentBasePrice = item.basePrice
            const quantity = item.quantity || item.basketItem?.quantity || 1

            // CRITICAL: If we have a preserved basePrice, ALWAYS use it
            // This ensures the original price from quantity 1 is always shown, just multiplied by quantity
            // Even if current item has a different basePrice, we use the preserved one
            if (
                preservedBasePrice != null &&
                !isNaN(preservedBasePrice) &&
                preservedBasePrice > 0
            ) {
                // ALWAYS override with preserved basePrice, even if current item has a different value
                return {
                    ...item,
                    basePrice: preservedBasePrice
                }
            }

            // If no preserved basePrice exists, store the current one if it's valid
            // This captures the original price when first seen (usually at quantity 1)
            // IMPORTANT: Check for > 0 to avoid storing 0 or invalid values
            if (
                currentBasePrice != null &&
                currentBasePrice !== undefined &&
                !isNaN(currentBasePrice) &&
                currentBasePrice > 0
            ) {
                // Store the per-unit basePrice - this will be used for all future quantities
                // Store by both itemId and productId for better matching
                preservedBasePricesRef.current.set(itemId, currentBasePrice)
                if (productId && productId !== itemId) {
                    preservedBasePricesRef.current.set(productId, currentBasePrice)
                }
                return {
                    ...item,
                    basePrice: currentBasePrice
                }
            }

            // If we still don't have a basePrice, try to infer it from basket item prices
            // This is a fallback when transformBasketData couldn't calculate it
            const basketItem = item.basketItem
            if (basketItem) {
                const basketPrice = basketItem.price != null ? parseFloat(basketItem.price) : null
                const discountedPrice =
                    basketItem.priceAfterItemDiscount != null
                        ? parseFloat(basketItem.priceAfterItemDiscount)
                        : basketPrice

                // If basket price > discounted price, basket price might be the original (per-unit)
                // Make sure we're using per-unit prices, not totals
                const quantity = basketItem.quantity || 1
                let potentialBasePrice = basketPrice

                // If price seems like a total (too high), divide by quantity to get per-unit
                if (
                    quantity > 1 &&
                    basketPrice != null &&
                    basketPrice > 200 &&
                    basketPrice / quantity < 250
                ) {
                    potentialBasePrice = basketPrice / quantity
                }

                if (
                    potentialBasePrice != null &&
                    discountedPrice != null &&
                    potentialBasePrice > discountedPrice &&
                    potentialBasePrice > 0
                ) {
                    // Found a valid basePrice from basket, preserve it
                    preservedBasePricesRef.current.set(itemId, potentialBasePrice)
                    return {
                        ...item,
                        basePrice: potentialBasePrice
                    }
                }
            }

            // Otherwise, use the item as-is (might not have a discount)
            return item
        })
    }, [transformedProductData])

    const transformedGiftCertificates = transformGiftCertificateItems(basket.giftCertificateItems)

    // Combine all items (products + gift certificates)
    const allTransformedItems = [
        ...transformedProductDataWithPreservedPrices,
        ...transformedGiftCertificates
    ]

    const hasNoItems =
        (!basket?.productItems || basket.productItems.length === 0) &&
        (!basket?.giftCertificateItems || basket.giftCertificateItems.length === 0)

    /********* Rendering  UI **********/
    if (isLoading || !allTransformedItems || (hasNoItems && isCartItemLoading)) {
        return <CartSkeleton />
    }

    if (!isLoading && hasNoItems) {
        return <EmptyCart isRegistered={isRegistered} previousSearchParams={previousSearchParams} />
    }

    const isMixedCart = (() => {
        const values = allTransformedItems
            .map((item) => item.c_storepickup)
            .filter((v) => v !== undefined)
        return new Set(values).size > 1
    })()

    const shipment = isMixedCart
        ? basket?.shipments?.find((s) => s?.c_shipmentType && s?.c_fromStoreId)
        : null

    const mixedCartProductMessage = shipment?.shippingMethod?.name || ''
    const mixedCartStaticMessage = shipment?.shippingMethod?.description || ''

    return (
        <GlobalErrorProvider>
            <Box flex="1" data-testid="sf-cart-container">
                <Container
                    paddingTop={{base: pxToRem(20), md: pxToRem(50)}}
                    paddingBottom={{base: pxToRem(0), md: pxToRem(45)}}
                    paddingLeft={{base: pxToRem(0), md: pxToRem(45)}}
                    paddingRight={{base: pxToRem(0), md: pxToRem(45)}}
                >
                    <Stack spacing={0}>
                        <CartTitle giftCardCount={transformedGiftCertificates?.length} />
                        <GlobalErrorMessage />
                        {cartLimitErrorMessage && (
                            <Box
                                sx={{
                                    width: '100%',
                                    px: {base: pxToRem(11), md: 0}
                                }}
                            >
                                <CartErrorMessage
                                    message={cartLimitErrorMessage}
                                    fullWidth
                                    marginBottom="2rem"
                                />
                            </Box>
                        )}
                        <Grid
                            templateColumns={{base: '1fr', lg: '51.56% 1fr'}}
                            gap={{base: 0, xl: pxToRem(58)}}
                        >
                            <GridItem>
                                <Stack spacing={0} sx={{mb: {base: '60px', md: 0}}}>
                                    {allTransformedItems?.map((transformedItem, idx) => {
                                        const productItem = transformedItem.isGiftCertificate
                                            ? transformedItem
                                            : transformedItem.basketItem || transformedItem

                                        if (!productItem && !transformedItem.isGiftCertificate)
                                            return null

                                        const currentQuantity = transformedItem.isGiftCertificate
                                            ? 1
                                            : getCurrentQuantity(
                                                  transformedItem.basketItem || transformedItem
                                              )

                                        const currentProduct = transformedItem.isGiftCertificate
                                            ? {
                                                  ...transformedItem,
                                                  itemId: transformedItem.id,
                                                  productId: transformedItem.id,
                                                  quantity: currentQuantity,
                                                  isGiftCertificate: true,
                                                  transformedProducts: allTransformedItems
                                              }
                                            : {
                                                  ...productItem,
                                                  ...(productsByItemId &&
                                                      productsByItemId[productItem.itemId]),
                                                  isProductUnavailable: !isProductsLoading
                                                      ? !productsByItemId?.[productItem.itemId]
                                                      : undefined,
                                                  price: productItem.price,
                                                  quantity: currentQuantity,
                                                  transformedProducts: allTransformedItems,
                                                  ...transformedItem
                                              }

                                        const isLastItem = idx === allTransformedItems.length - 1

                                        return (
                                            <React.Fragment
                                                key={transformedItem.itemId || transformedItem.id}
                                            >
                                                <Box
                                                    position="relative"
                                                    sx={{
                                                        mb: {base: '10px', lg: '12.5px'},
                                                        px: {base: pxToRem(11)}
                                                    }}
                                                >
                                                    <ProductItem
                                                        index={idx}
                                                        secondaryActions={
                                                            <CartSecondaryButtonGroup
                                                                isAGift={
                                                                    localIsGiftItems[
                                                                        productItem.itemId
                                                                    ]
                                                                        ? localIsGiftItems[
                                                                              productItem.itemId
                                                                          ]
                                                                        : productItem.gift
                                                                }
                                                                onIsAGiftChange={
                                                                    handleIsAGiftChange
                                                                }
                                                                onAddToWishlistClick={
                                                                    handleAddToWishlist
                                                                }
                                                                onEditClick={(product) => {
                                                                    setSelectedItem(product)
                                                                    onOpen()
                                                                }}
                                                                onRemoveItemClick={handleRemoveItem}
                                                                isEGiftCard={
                                                                    !!transformedItem.isGiftCertificate
                                                                }
                                                            />
                                                        }
                                                        product={currentProduct}
                                                        onItemQuantityChange={handleChangeItemQuantity.bind(
                                                            this,
                                                            transformedItem.basketItem ||
                                                                productItem
                                                        )}
                                                        showLoading={
                                                            isCartItemLoading &&
                                                            selectedItem?.itemId ===
                                                                productItem.itemId
                                                        }
                                                        handleRemoveItem={handleRemoveItem}
                                                        disableQuantityIncrease={false}
                                                        isMixedCart={isMixedCart}
                                                        mixedCartProductMessage={
                                                            mixedCartProductMessage
                                                        }
                                                    />
                                                </Box>
                                                {!isLastItem && (
                                                    <Box sx={{mb: {base: '10px', lg: '12.5px'}}}>
                                                        <Divider sx={dividorStyle} />
                                                    </Box>
                                                )}
                                            </React.Fragment>
                                        )
                                    })}
                                </Stack>
                                <Box>
                                    {isOpen && !selectedItem.bundledProductItems && (
                                        <QuickView
                                            isOpen={isOpen}
                                            onClose={onClose}
                                            product={selectedItem}
                                            previousSearchParams={previousSearchParams}
                                            updateCart={(variant, quantity, storeId, storepickup) =>
                                                handleUpdateCart(
                                                    variant,
                                                    quantity,
                                                    storeId,
                                                    storepickup
                                                )
                                            }
                                        />
                                    )}
                                    {isOpen && selectedItem.bundledProductItems && (
                                        <BundleProductViewModal
                                            isOpen={isOpen}
                                            onOpen={onOpen}
                                            onClose={onClose}
                                            product={selectedItem}
                                            updateCart={(product, quantity, childProducts) =>
                                                handleUpdateBundle(product, quantity, childProducts)
                                            }
                                        />
                                    )}
                                </Box>
                            </GridItem>
                            <GridItem>
                                <Box display="flex" flexDirection="column">
                                    <Stack
                                        boxShadow={`${pxToRem(2)} ${pxToRem(4)} ${pxToRem(
                                            16.6
                                        )} ${pxToRem(2)} ${colors.blackAlpha[500]}`}
                                        paddingLeft={pxToRem(18)}
                                        paddingRight={pxToRem(18)}
                                        sx={{
                                            order: 1,
                                            '@media (min-width: 1024px)': {
                                                order: 0
                                            }
                                        }}
                                        paddingBottom={[pxToRem(20), pxToRem(20), '0px', '0px']}
                                        marginTop={['0', pxToRem(30), '0', '0']}
                                    >
                                        <OrderSummary
                                            showPromoCodeForm={true}
                                            isEstimate={true}
                                            basket={basket}
                                            isCart={true}
                                        />
                                        <Box marginTop={0}>
                                            <CartCta
                                                basket={basket}
                                                updateBasket={updateBasket}
                                                isMixedCart={isMixedCart}
                                                mixedCartStaticMessage={mixedCartStaticMessage}
                                                shippingMethods={shippingOptions}
                                            />
                                        </Box>
                                    </Stack>
                                    <ContentRenderer
                                        contentIds="cart-banner"
                                        isSlot={true}
                                        sx={{
                                            ...cartBannerStyle,
                                            order: 0,
                                            '@media (min-width: 1024px)': {
                                                order: 1
                                            }
                                        }}
                                        paddingLeft={[
                                            pxToRem(11),
                                            pxToRem(11),
                                            pxToRem(0),
                                            pxToRem(0)
                                        ]}
                                        paddingRight={[
                                            pxToRem(11),
                                            pxToRem(11),
                                            pxToRem(0),
                                            pxToRem(0)
                                        ]}
                                    />
                                </Box>
                            </GridItem>
                        </Grid>
                        {/* Product Recommendations */}
                        <Stack
                            pt={{base: pxToRem(60), lg: pxToRem(80)}}
                            px={{base: pxToRem(11), lg: pxToRem(0)}}
                        >
                            <RecommendedProducts
                                containerCustomStyle={{pb: 0}}
                                title={recommendedForYou}
                                recommender={CART_RECOMMENDATION}
                                previousSearchParams={previousSearchParams}
                                fetchKey={
                                    !isProductsLoading &&
                                    Object.values(products)[Object.values(products).length - 1]?.id
                                }
                                products={
                                    !isProductsLoading && [
                                        Object.values(products)[Object.values(products).length - 1]
                                    ]
                                }
                            />
                        </Stack>
                    </Stack>
                </Container>
                <ConfirmationModal
                    {...REMOVE_CART_ITEM_CONFIRMATION_DIALOG_CONFIG}
                    onPrimaryAction={() => {
                        handleRemoveItem(selectedItem)
                    }}
                    onAlternateAction={() => {}}
                    {...modalProps}
                />

                <UnavailableProductConfirmationModal
                    productItems={basket?.productItems}
                    handleUnavailableProducts={handleUnavailableProducts}
                />
            </Box>
        </GlobalErrorProvider>
    )
}

Cart.getTemplateName = () => 'cart'

export default Cart
